import React, { useState } from 'react';
import { TouchableOpacity } from 'react-native-gesture-handler';

import Clipboard from '@react-native-clipboard/clipboard';
import { useNavigation } from '@react-navigation/native';

import { Button } from '@/components/ButtonComponent';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { TextInput } from '@/components/TextInput';
import { Column, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { getCasinoGamesByIds } from '@/feature/lobby-casino/hooks/use-casino-game-history';
import { useFormattedCasinoGameUrl } from '@/feature/lobby-casino/hooks/use-formatted-casino-game-url';
import { useSelectedCountryCode } from '@/feature/lobby-casino/hooks/use-selected-country-code';
import { useQuery } from '@tanstack/react-query';

/**
 * Dev controls to launch a casino game by ID, or generate its session URL
 * (cash or coins, depending on the currently active currency mode) as
 * copyable text for debugging without navigating to the WebView screen.
 */
export const CasinoGameLauncher = () => {
    const { navigate } = useNavigation();
    const { showToast } = useAlerts();
    const [casinoGameId, setCasinoGameId] = useState('');
    const [generateUrlGameId, setGenerateUrlGameId] = useState<string | null>(null);
    const selectedCasinoCountryCode = useSelectedCountryCode();

    const { data: generateUrlGameData, isFetching: isGenerateUrlGameFetching } = useQuery({
        queryKey: ['casino-game-by-id', generateUrlGameId, selectedCasinoCountryCode],
        queryFn: () => getCasinoGamesByIds(generateUrlGameId ? [generateUrlGameId] : [], selectedCasinoCountryCode),
        enabled: !!generateUrlGameId,
    });
    const generateUrlLaunchUrl = generateUrlGameData?.items[0]?.launchUrl;
    const generatedCasinoGameUrl = useFormattedCasinoGameUrl(generateUrlLaunchUrl);
    const generateUrlGameNotFound =
        !!generateUrlGameId && !isGenerateUrlGameFetching && generateUrlGameData?.items.length === 0;

    return (
        <Column p={'s12'} m={'s8'} borderRadius={'r12'} borderWidth={1} borderColor={ds.colorToken.border.strong}>
            <Text>Launch a casino game by ID</Text>
            <TextInput value={casinoGameId} label={'Casino Game ID'} onChangeText={setCasinoGameId} />
            <SizedBox value={8} />
            <Row gap={'s8'}>
                <Button
                    label={'Launch Casino Game'}
                    disabled={!casinoGameId.trim()}
                    onPress={() => {
                        const trimmedId = casinoGameId.trim();
                        if (!trimmedId) {
                            return;
                        }
                        navigate('CasinoGameWebView', { gameId: trimmedId });
                    }}
                />
                <Button
                    label={'Generate URL'}
                    disabled={!casinoGameId.trim()}
                    onPress={() => {
                        const trimmedId = casinoGameId.trim();
                        if (!trimmedId) {
                            return;
                        }
                        setGenerateUrlGameId(trimmedId);
                    }}
                />
            </Row>
            {generateUrlGameId ? (
                <>
                    <SizedBox value={8} />
                    {isGenerateUrlGameFetching || (!generatedCasinoGameUrl && !generateUrlGameNotFound) ? (
                        <Text color={ds.colorToken.text.secondary}>Generating URL…</Text>
                    ) : generateUrlGameNotFound ? (
                        <Text color={ds.colorToken.text.secondary}>
                            {`No game found for ID "${generateUrlGameId}"`}
                        </Text>
                    ) : generatedCasinoGameUrl ? (
                        <TouchableOpacity
                            onPress={() => {
                                Clipboard.setString(generatedCasinoGameUrl);
                                showToast({ message: 'Casino game URL copied to clipboard.' });
                            }}
                        >
                            <Text>{generatedCasinoGameUrl}</Text>
                        </TouchableOpacity>
                    ) : null}
                </>
            ) : null}
        </Column>
    );
};
